import { animate, createScope, utils } from "animejs";
import { useEffect, useRef } from "react";

interface NeuralLoaderProps {
	size?: "sm" | "md" | "lg" | "full";
	text?: string;
	fullscreen?: boolean;
	backgroundColor?: "black" | "white" | "grey" | "none";
	networkColor?: "black" | "white" | "grey" | "red" | "green";
}

export const NeuralLoader: React.FC<NeuralLoaderProps> = ({ 
	size = "full", 
	text, 
	fullscreen = true, 
	backgroundColor = "black", 
	networkColor = "white" 
}) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const scopeRef = useRef<any>(null);

	const sizeMap = {
		sm: 120,
		md: 200,
		lg: 320,
		full: 400,
	};

	// Background color mappings
	const backgroundColorMap = {
		black: "bg-black/80 backdrop-blur-sm",
		white: "bg-white/90 backdrop-blur-sm",
		grey: "bg-gray-500/40 backdrop-blur-sm",
		none: ""
	};

	// Network color mappings
	const networkColorMap = {
		white: "rgba(255, 255, 255, 0.12)",
		black: "rgba(0, 0, 0, 0.25)",
		grey: "rgba(128, 128, 128, 0.20)",
		red: "rgba(239, 68, 68, 0.20)",
		green: "rgba(34, 197, 94, 0.20)"
	};

	// Node color mappings (for the breathing nodes)
	const nodeColorMap = {
		white: "border-white",
		black: "border-black",
		grey: "border-gray-500",
		red: "border-red-500",
		green: "border-green-500"
	};

	const radius = sizeMap[size];
	const canvasSize = radius * 2.4;

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const center = canvasSize / 2;
		const nodeCount = 80;
		const connectionDistance = radius * 0.65;

		interface Node {
			x: number;
			y: number;
			angle: number;
			distance: number;
			element: HTMLElement;
		}

		const nodes: Node[] = [];
		const connections: Array<[number, number]> = [];

		const layers = [
			{ count: 24, minDist: radius * 0.85, maxDist: radius * 0.98 },
			{ count: 20, minDist: radius * 0.65, maxDist: radius * 0.82 },
			{ count: 16, minDist: radius * 0.45, maxDist: radius * 0.62 },
			{ count: 12, minDist: radius * 0.25, maxDist: radius * 0.42 },
			{ count: 8, minDist: radius * 0.05, maxDist: radius * 0.22 },
		];

		layers.forEach((layer) => {
			const angleStep = (Math.PI * 2) / layer.count;
			for (let i = 0; i < layer.count; i++) {
				const angle = i * angleStep + utils.random(-angleStep * 0.3, angleStep * 0.3);
				const distance = utils.random(layer.minDist, layer.maxDist);
				const x = center + Math.cos(angle) * distance;
				const y = center + Math.sin(angle) * distance;

				const nodeEl = document.createElement("div");
				nodeEl.className = "neural-node";
				nodeEl.style.cssText = `
          position: absolute;
          width: 5px;
          height: 5px;
          background: white;
          border-radius: 50%;
          left: ${x}px;
          top: ${y}px;
          transform: translate(-50%, -50%);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.8), 0 0 20px rgba(255, 255, 255, 0.4);
        `;
				rootRef.current?.querySelector(".neural-container")?.appendChild(nodeEl);

				nodes.push({ x, y, angle, distance, element: nodeEl });
			}
		});

		for (let i = 0; i < nodes.length; i++) {
			for (let j = i + 1; j < nodes.length; j++) {
				const dx = nodes[i].x - nodes[j].x;
				const dy = nodes[i].y - nodes[j].y;
				const dist = Math.sqrt(dx * dx + dy * dy);

				if (dist < connectionDistance) {
					connections.push([i, j]);
				}
			}
		}

		const drawConnections = () => {
			ctx.clearRect(0, 0, canvasSize, canvasSize);

			ctx.strokeStyle = networkColorMap[networkColor];
			ctx.lineWidth = 1;

			connections.forEach(([i, j]) => {
				ctx.beginPath();
				ctx.moveTo(nodes[i].x, nodes[i].y);
				ctx.lineTo(nodes[j].x, nodes[j].y);
				ctx.stroke();
			});
		};

		drawConnections();

		scopeRef.current = createScope({ root: rootRef.current }).add((self) => {
			const breathingContainer = rootRef.current?.querySelector(".breathing-container");
			if (breathingContainer) {
				animate(breathingContainer, {
					scale: [
						{ to: 1.05, duration: 3500, ease: "inOut(2)" },
						{ to: 1, duration: 3500, ease: "inOut(2)" },
					],
					loop: true,
				});

				animate(breathingContainer, {
					rotate: 360,
					duration: 60000,
					loop: true,
					ease: "linear",
				});
			}

			animate(".neural-node", {
				scale: [
					{ to: [1, 1.6], duration: () => utils.random(700, 1600) },
					{ to: 1, duration: () => utils.random(400, 900) },
				],
				opacity: [
					{ to: [1, 0.3], duration: () => utils.random(600, 1400) },
					{ to: 1, duration: () => utils.random(400, 800) },
				],
				loop: true,
				delay: () => utils.random(0, 2500),
			});

			connections.forEach(([i, j], idx) => {
				const pulseEl = document.createElement("div");
				pulseEl.className = `pulse-${idx}`;
				pulseEl.style.cssText = `
          position: absolute;
          width: 6px;
          height: 6px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 0 16px rgba(255, 255, 255, 1), 0 0 32px rgba(255, 255, 255, 0.6);
          pointer-events: none;
          opacity: 0;
        `;
				rootRef.current?.querySelector(".neural-container")?.appendChild(pulseEl);

				const dx = nodes[j].x - nodes[i].x;
				const dy = nodes[j].y - nodes[i].y;
				const distance = Math.sqrt(dx * dx + dy * dy);
				const steps = Math.ceil(distance / 1.5);

				animate(pulseEl, {
					x: Array.from({ length: steps }, (_, k) => {
						return nodes[i].x + (dx * k) / (steps - 1);
					}),
					y: Array.from({ length: steps }, (_, k) => {
						return nodes[i].y + (dy * k) / (steps - 1);
					}),
					scale: [
						{ to: 1.4, duration: 120 },
						{ to: 0.8, duration: () => distance * 1.2 },
						{ to: 1, duration: 180 },
					],
					opacity: [
						{ to: 1, duration: 80 },
						{ to: 0.95, duration: () => distance * 1.3 },
						{ to: 0, duration: 160 },
					],
					loop: true,
					duration: () => distance * 2.2 + utils.random(600, 1800),
					delay: () => utils.random(0, 3500),
				});
			});

			const circleEl = rootRef.current?.querySelector(".neural-circle");
			if (circleEl) {
				animate(circleEl, {
					rotate: 360,
					duration: 28000,
					loop: true,
					ease: "linear",
				});

				animate(circleEl, {
					scale: [
						{ to: 1.015, duration: 2200, ease: "inOut(2)" },
						{ to: 1, duration: 2200, ease: "inOut(2)" },
					],
					opacity: [
						{ to: 0.4, duration: 2200, ease: "inOut(2)" },
						{ to: 0.25, duration: 2200, ease: "inOut(2)" },
					],
					loop: true,
				});
			}

			const innerRingEl = rootRef.current?.querySelector(".inner-ring");
			if (innerRingEl) {
				animate(innerRingEl, {
					rotate: -360,
					duration: 22000,
					loop: true,
					ease: "linear",
				});

				animate(innerRingEl, {
					opacity: [
						{ to: 0.35, duration: 1600, ease: "inOut(3)" },
						{ to: 0.18, duration: 1600, ease: "inOut(3)" },
					],
					loop: true,
				});
			}

			const middleRingEl = rootRef.current?.querySelector(".middle-ring");
			if (middleRingEl) {
				animate(middleRingEl, {
					rotate: 360,
					duration: 25000,
					loop: true,
					ease: "linear",
				});

				animate(middleRingEl, {
					opacity: [
						{ to: 0.28, duration: 1900, ease: "inOut(2)" },
						{ to: 0.12, duration: 1900, ease: "inOut(2)" },
					],
					loop: true,
				});
			}

			const outerCircleEl = rootRef.current?.querySelector(".outer-circle");
			if (outerCircleEl) {
				animate(outerCircleEl, {
					rotate: -360,
					duration: 35000,
					loop: true,
					ease: "linear",
				});

				animate(outerCircleEl, {
					opacity: [
						{ to: 0.7, duration: 2500, ease: "inOut(3)" },
						{ to: 0.4, duration: 2500, ease: "inOut(3)" },
					],
					scale: [
						{ to: 1.008, duration: 2500, ease: "inOut(3)" },
						{ to: 1, duration: 2500, ease: "inOut(3)" },
					],
					loop: true,
				});
			}
		});

		return () => {
			scopeRef.current?.revert();
			rootRef.current?.querySelectorAll('.neural-node, [class^="pulse-"]').forEach((el) => el.remove());
		};
	}, [canvasSize, radius, size]);

	const containerClasses = fullscreen 
		? `fixed inset-0 flex items-center justify-center z-50 ${backgroundColorMap[backgroundColor]}`
		: "flex items-center justify-center w-full h-full";

	return (
		<div ref={rootRef} className={containerClasses}>
			<div className="flex flex-col items-center gap-6">
				<div className="breathing-container relative flex items-center justify-center" style={{ width: canvasSize, height: canvasSize }}>
					<div
						className={`outer-circle absolute rounded-full border-2 ${nodeColorMap[networkColor]}`}
						style={{
							width: radius * 2,
							height: radius * 2,
							opacity: 0.4,
							boxShadow: "0 0 20px rgba(255, 255, 255, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1)",
						}}
					/>
					<div
						className={`neural-circle absolute rounded-full border ${nodeColorMap[networkColor].replace('border-', 'border-').replace('500', '200')}/25`}
						style={{
							width: radius * 1.85,
							height: radius * 1.85,
						}}
					/>
					<div
						className={`inner-ring absolute rounded-full border-2 ${nodeColorMap[networkColor].replace('border-', 'border-').replace('500', '200')}/20`}
						style={{
							width: radius * 1.6,
							height: radius * 1.6,
						}}
					/>
					<div
						className={`middle-ring absolute rounded-full border ${nodeColorMap[networkColor].replace('border-', 'border-').replace('500', '200')}/15`}
						style={{
							width: radius * 1.3,
							height: radius * 1.3,
						}}
					/>
					<canvas ref={canvasRef} width={canvasSize} height={canvasSize} className="absolute top-0 left-0" style={{ mixBlendMode: "screen" }} />
					<div className="neural-container absolute" style={{ width: canvasSize, height: canvasSize }} />
				</div>
				{text && (
					<div 
						className={`text-sm font-medium tracking-wider uppercase animate-pulse ${
							backgroundColor === 'white' ? 'text-gray-800' : 'text-white/90'
						}`}
					>
						{text}
					</div>
				)}
			</div>
		</div>
	);
};
